import {
  FC,
  useContext,
  createContext,
  useState,
  useEffect,
  PropsWithChildren,
} from 'react';
import { useQuery } from '@tanstack/react-query';

import { CONFIG } from '../../api/config';
import { fetchAuthenticated } from '../../api/endpoints/authentication';
import { ERROR } from '../../api/enums';
import { IErrorResponse } from '../../api/interfaces';
import {
  IAuthenticatedSuccessResponse,
  IAuthenticatedUser,
} from '../../api/interfaces/authentication';
import { getPaginatedCustomersApi } from '../../api/endpoints/customer';

const AuthenticatedUserContext = createContext<IAuthenticatedUser | object>({});

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState<IAuthenticatedUser | null>(
    null,
  );

  const { data, error } = useQuery<
    IAuthenticatedSuccessResponse,
    IErrorResponse
  >({
    queryKey: ['authenticated'],
    queryFn: fetchAuthenticated,
    retry: false,
  });

  const { error: customerError } = useQuery({
    queryKey: ['paginatedCustomers'],
    queryFn: () => getPaginatedCustomersApi(),
    useErrorBoundary: false,
    retry: false,
  });

  const redirectToManagerPortal = () => {
    setAuthenticated(null);
    window.location.href = CONFIG.MANAGER_PORTAL;
  };

  useEffect(() => {
    if (data) {
      setAuthenticated(data);
    }
  }, [data]);

  useEffect(() => {
    if (error?.response?.data.error === ERROR.UNAUTHORIZED || customerError) {
      redirectToManagerPortal();
    }
  }, [error, customerError]);

  return (
    authenticated && (
      <AuthenticatedUserContext.Provider value={authenticated}>
        {children}
      </AuthenticatedUserContext.Provider>
    )
  );
};

const useUser = () => useContext<IAuthenticatedUser>(AuthenticatedUserContext);

export { useUser };
export default AuthProvider;
